import { Component, OnDestroy, OnInit } from '@angular/core';
import { DigitalVacancyModel } from '@digilize/shared/definitions/src';
import { BehaviorSubject, of, Subject, takeUntil } from 'rxjs';
import { VacaniesService } from '../../../../../services/vacanies.service';
import { Vacancy } from '../../models/vacancy.model';
import { VacancyDetailData } from '../vacancy-detail/vacancy-detail.component';

@Component({
  selector: 'bwc-saved-vacancies',
  templateUrl: './saved-vacancies.component.html',
  styleUrls: ['./saved-vacancies.component.scss'],
})
export class SavedVacanciesComponent implements OnInit, OnDestroy {
  savedVacancies$ = new BehaviorSubject<Vacancy[]>([]);
  loading = false;
  detailData: VacancyDetailData | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(private vacaniesService: VacaniesService) {}

  ngOnInit(): void {
    this.loadSaved();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get savedVacancies(): Vacancy[] {
    return this.savedVacancies$.getValue();
  }

  unlike(vacancy: Vacancy): void {
    this.vacaniesService.toggleFavoriteVacancy(vacancy.id).subscribe();
    this.savedVacancies$.next(this.savedVacancies.filter((v) => v.id !== vacancy.id));
  }

  openDetail(vacancy: Vacancy): void {
    const index = this.savedVacancies.findIndex((v) => v.id === vacancy.id);
    if (index < 0) return;
    this.detailData = {
      vacancies$: this.savedVacancies$.asObservable(),
      initialIndex: index,
      hasMore$: of(false),
      onFavoriteToggled: (id, isSaved) => {
        if (!isSaved) {
          const idStr = String(id);
          this.savedVacancies$.next(this.savedVacancies.filter((v) => String(v.id) !== idStr));
        }
      },
    };
  }

  closeDetail(): void {
    this.detailData = null;
  }

  private loadSaved(): void {
    this.loading = true;
    this.vacaniesService
      .getMyFavoriteVacancies()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.savedVacancies$.next((res.data || []).map((dto) => this.mapToVacancy(dto)));
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private mapToVacancy(dto: DigitalVacancyModel): Vacancy {
    const now = new Date();
    const activated = dto.activated ? new Date(dto.activated) : null;
    const diffDays = activated ? Math.floor((now.getTime() - activated.getTime()) / (1000 * 60 * 60 * 24)) : Infinity;

    return {
      id: dto.id as unknown as number,
      title: dto.job_title || '',
      company: dto.organization || '',
      location: dto.location || '',
      tags: dto.tags || [],
      skills: [],
      skillCategories: { knowledge: [], softSkills: [], languages: [] },
      matchScore: dto.match_score ?? 0,
      isNew: diffDays <= 7,
    };
  }
}
